import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/UI/Button';
import { Stack } from '@/components/UI/Stack';
import { IconFont } from '@/components/IconFont';
import {
	dedupeMergeTagKeys,
	getMergeTagGroup,
	getMergeTagGroupLabel,
	getMergeTagLabel,
	hasMergeTagKey,
} from '@bay-email/core';
import clsx from 'clsx';
import './TestVariablesModal.scss';

const SIGNATURE_BLOCK_LOGO_URL =
	'https://bay.cdn.bkat.io/cust/bay_demo/images/logos/cbaq01.png?v=64958';
const USER_LOGO_DARK_TEST_URL =
	'https://bay.cdn.bkat.io/cust/bay_demo/images/logos/cbaq01.png?v=64958';
const USER_LOGO_LIGHT_TEST_URL =
	'https://staging-bay.cdn.bkat.io/cust/bay_staging/images/logos/realistiq.20260603133246600.light.png?v=18745';
const OFFICE_LOGO_DARK_TEST_URL =
	'https://bay.cdn.bkat.io/cust/bay_demo/images/logos/cbaq01.png?v=64958';
const OFFICE_LOGO_LIGHT_TEST_URL =
	'https://staging-bay.cdn.bkat.io/cust/bay_staging/images/logos/realistiq.20260603133246600.light.png?v=18745';

const DOWNSTREAM_RESOLVED_MERGE_TAG_LEAF_KEYS = new Set([
	'unsubscribelink',
	'campaigndisclaimer',
]);

function isDownstreamResolvedMergeTagKey(key: string): boolean {
	const normalizedKey = String(key || '').trim();
	if (!normalizedKey) return false;

	const leafKey = normalizedKey.split('.').pop() || normalizedKey;
	return DOWNSTREAM_RESOLVED_MERGE_TAG_LEAF_KEYS.has(leafKey.toLowerCase());
}

// Merge-tag keys intentionally hidden from the Test Variables editor.
const HIDDEN_MERGE_TAG_KEYS = new Set(['user.companylogo']);

function isHiddenMergeTagKey(key: string): boolean {
	return HIDDEN_MERGE_TAG_KEYS.has(
		String(key || '')
			.trim()
			.toLowerCase(),
	);
}

function sanitizeDownstreamResolvedTestValues(
	values: Record<string, string>,
): Record<string, string> {
	const sanitized: Record<string, string> = {};

	Object.entries(values || {}).forEach(([key, value]) => {
		if (isDownstreamResolvedMergeTagKey(key)) return;
		sanitized[key] = value;
	});

	return sanitized;
}

// Sub-grouping for the User Data section: every `user.*` field renders under one
// of two sub-headers — Office (location details + office logos) or Profile
// (everything else). Keys are the lowercased leaf of a `user.*` merge tag.
type UserSubgroup = 'profile' | 'office';
const OFFICE_LEAVES = new Set([
	'officeaddress',
	'officecity',
	'officestate',
	'officezip',
	'officename',
	'officedomain',
	'officelogodarkurl',
	'officelogolighturl',
]);

function getUserSubgroup(key: string): UserSubgroup | null {
	const normalized = String(key || '')
		.trim()
		.toLowerCase();
	if (!normalized.startsWith('user.')) return null;
	const leaf = normalized.split('.').pop() || '';
	return OFFICE_LEAVES.has(leaf) ? 'office' : 'profile';
}

// Explicit input ordering within each sub-section. Leaves not listed fall to the
// end (then sorted by label as a tiebreak by the caller).
const PROFILE_FIELD_ORDER = [
	'certifications',
	'companyname',
	'primaryphone',
	'email',
	'firstname',
	'fullname',
	'lastname',
	'licensenumber',
	'mobilephone',
	'team',
	'title',
	'website',
	'profileimagecircle',
	'profileimageportrait',
	'profileimagesquare',
	'logodarkurl',
	'logolighturl',
];
const OFFICE_FIELD_ORDER = [
	'officeaddress',
	'officecity',
	'officestate',
	'officezip',
	'officename',
	'officedomain',
	'officelogodarkurl',
	'officelogolighturl',
];

function getFieldOrder(order: string[], key: string): number {
	const leaf = String(key || '')
		.trim()
		.toLowerCase()
		.split('.')
		.pop() || '';
	const index = order.indexOf(leaf);
	return index === -1 ? order.length : index;
}

export interface TestVariablesModalProps {
	isVisible: boolean;
	onClose: () => void;
	mergeTags: Record<string, any>;
	// Pristine defaults (no kvstore/preview overrides) used by "Reset to Defaults".
	baseMergeTags?: Record<string, any>;
	testValues: Record<string, string>;
	onTestValuesChange: (values: Record<string, string>) => void;
	// Called on "Reset to Defaults" with the pristine values so the host can
	// wipe persisted (kvstore) overrides immediately.
	onTestValuesReset?: (values: Record<string, string>) => void;
	// Recipient-real preview (see PropsProvider.recipientPreview): user/office
	// fields are facts about the sender overlaid from the real profile, so their
	// test inputs are hidden — editing them could only produce a preview that
	// will never be sent.
	hideUserFields?: boolean;
}

export function TestVariablesModal({
	isVisible,
	onClose,
	mergeTags,
	baseMergeTags,
	testValues,
	onTestValuesChange,
	onTestValuesReset,
	hideUserFields,
}: TestVariablesModalProps) {
	const [localValues, setLocalValues] = useState<Record<string, string>>(testValues);

	// Sync local values when testValues prop changes (e.g., when parent resets)
	useEffect(() => {
		setLocalValues(testValues);
	}, [testValues]);

	const getFieldLabel = useCallback(
		(groupKey: string, fieldKey: string): string | null => {
			const fullKey = `${groupKey}.${fieldKey}`;
			return getMergeTagLabel(fullKey);
		},
		[],
	);

	// Flatten merge tags structure for easier editing
	const flattenedTags = useMemo(() => {
		const flattened: Array<{ key: string; label: string; group: string }> = [];
		const seenKeys = new Set<string>();
		const listingImageKeys = Array.from(
			{ length: 10 },
			(_, index) => `listing.image_data_${index}`,
		);

		const tryAddField = (key: string, label: string | null, groupLabel: string) => {
			const normalizedKey = String(key || '').trim();
			if (!normalizedKey || seenKeys.has(normalizedKey) || !label) return;
			if (isHiddenMergeTagKey(normalizedKey)) return;
			// Covers both the Profile and Office sub-sections — everything under
			// user.* is sender fact, not test data, when hideUserFields is on.
			if (hideUserFields && normalizedKey.toLowerCase().startsWith('user.')) return;

			seenKeys.add(normalizedKey);
			flattened.push({
				key: normalizedKey,
				label,
				group: groupLabel,
			});
		};

		// Add merge tags from props, filtered by dataFields configuration
		Object.entries(mergeTags).forEach(([groupKey, groupData]) => {
			if (typeof groupData === 'object' && groupData !== null) {
				dedupeMergeTagKeys(
					Object.keys(groupData).map(fieldKey => `${groupKey}.${fieldKey}`),
				).forEach(dedupedFullKey => {
					const fieldKey = dedupedFullKey.split('.').slice(1).join('.');
					const fullKey = `${groupKey}.${fieldKey}`;

					if (hasMergeTagKey(fullKey)) {
						const label = getFieldLabel(groupKey, fieldKey);
						tryAddField(fullKey, label, getMergeTagGroupLabel(groupKey));
					}
				});
				return;
			}

			if (typeof groupData === 'string' || typeof groupData === 'number') {
				if (!hasMergeTagKey(groupKey)) return;

				const resolvedGroupKey = getMergeTagGroup(groupKey);
				tryAddField(
					groupKey,
					getMergeTagLabel(groupKey),
					getMergeTagGroupLabel(resolvedGroupKey),
				);
			}
		});

		if (mergeTags.listing && typeof mergeTags.listing === 'object') {
			listingImageKeys.forEach((fullKey, index) => {
				if (flattened.some(tag => tag.key === fullKey)) return;

				const fieldKey = fullKey.replace('listing.', '');
				const label = getFieldLabel('listing', fieldKey) || `Image ${index + 1}`;

				flattened.push({
					key: fullKey,
					label,
					group: getMergeTagGroupLabel('listing'),
				});
			});
		}

		return flattened;
	}, [mergeTags, getFieldLabel, hideUserFields]);

	// Group tags by category
	const groupedTags = useMemo(() => {
		const grouped: Record<string, Array<{ key: string; label: string }>> = {};

		const getListingImageIndex = (key: string) => {
			const match = key.match(/^listing\.image_data_(\d+)$/);
			return match ? Number(match[1]) : null;
		};

		flattenedTags.forEach(tag => {
			if (!grouped[tag.group]) {
				grouped[tag.group] = [];
			}
			grouped[tag.group].push({ key: tag.key, label: tag.label });
		});

		Object.keys(grouped).forEach(groupName => {
			grouped[groupName].sort((a, b) => {
				const aImageIndex = getListingImageIndex(a.key);
				const bImageIndex = getListingImageIndex(b.key);

				if (aImageIndex !== null && bImageIndex !== null) {
					return aImageIndex - bImageIndex;
				}

				if (aImageIndex !== null && bImageIndex === null) {
					return 1;
				}

				if (aImageIndex === null && bImageIndex !== null) {
					return -1;
				}

				return a.label.localeCompare(b.label, undefined, {
					numeric: true,
					sensitivity: 'base',
				});
			});
		});

		return grouped;
	}, [flattenedTags]);

	const handleInputChange = useCallback((key: string, value: string) => {
		setLocalValues(prev => ({ ...prev, [key]: value }));
	}, []);

	const handleSave = useCallback(() => {
		onTestValuesChange(sanitizeDownstreamResolvedTestValues(localValues));
		onClose();
	}, [localValues, onTestValuesChange, onClose]);

	// Last-resort sample values for fields baseMergeTags doesn't define.
	// Real defaults live in the host's mergeTags object — do NOT add
	// hardcoded person/place data here.
	const generateSampleValue = useCallback((fieldKey: string): string => {
		const key = fieldKey.toLowerCase();
		if (key.includes('name') && !key.includes('file') && !key.includes('user'))
			return 'Sample Name';
		if (key.includes('email')) return 'sample@example.com';
		if (key.includes('phone')) return '(555) 123-4567';
		if (key.includes('address')) return '123 Main St';
		if (key.includes('city')) return 'Sample City';
		if (key.includes('state')) return 'CA';
		if (key.includes('zip') || key.includes('postal')) return '94105';
		if (key.includes('title')) return 'Sample Title';
		if (key.includes('officelogolighturl')) return OFFICE_LOGO_LIGHT_TEST_URL;
		if (key.includes('officelogodarkurl')) return OFFICE_LOGO_DARK_TEST_URL;
		if (key.includes('logolighturl')) return USER_LOGO_LIGHT_TEST_URL;
		if (key.includes('logodarkurl')) return USER_LOGO_DARK_TEST_URL;
		if (key.includes('logo')) return SIGNATURE_BLOCK_LOGO_URL;
		if (key.includes('company') || key.includes('office')) return 'Sample Company';
		if (key.includes('price') || key.includes('commission')) return '$299,999';
		if (key.includes('image') || key.includes('photo'))
			return 'https://placehold.co/300x200';
		if (key.includes('url') || key.includes('link') || key.includes('website'))
			return 'https://example.com';
		if (key.includes('description'))
			return 'This is a sample description for testing purposes.';
		if (key.includes('date') || key.includes('birth')) return '01/15/1985';
		if (key.includes('team')) return 'Sample Team';

		// Default fallback
		return `Sample ${fieldKey.split('.').pop()?.charAt(0).toUpperCase()}${fieldKey.split('.').pop()?.slice(1) || ''}`;
	}, []);

	// Resolve a field's PRISTINE default from the base merge tags (e.g.
	// `user.licenseNumber` -> baseMergeTags.user.licenseNumber). Reads from
	// baseMergeTags so "Reset to Defaults" restores the true sample data and
	// ignores any kvstore/preview overrides merged into `mergeTags`. Returns
	// undefined when the source has no value, so callers can fall back.
	const resetSource = baseMergeTags || mergeTags;
	const resolveFromMergeTags = useCallback(
		(fieldKey: string): string | undefined => {
			const parts = fieldKey.split('.');
			let current: unknown = resetSource;
			for (const part of parts) {
				if (typeof current !== 'object' || current === null) return undefined;
				current = (current as Record<string, unknown>)[part];
			}
			if (current === undefined || current === null) return undefined;
			if (typeof current === 'object') return undefined;
			return String(current);
		},
		[resetSource],
	);

	const handleReset = useCallback(() => {
		const resetValues: Record<string, string> = {};
		flattenedTags.forEach(tag => {
			if (isDownstreamResolvedMergeTagKey(tag.key)) return;
			// Prefer the pristine base default (single source of truth); fall back
			// to generated sample values only for fields the base doesn't define.
			resetValues[tag.key] =
				resolveFromMergeTags(tag.key) ?? generateSampleValue(tag.key);
		});
		// Replace (don't merge) so stale keys from old saved values are dropped.
		setLocalValues(resetValues);
		// Let the host wipe persisted kvstore overrides so the reset sticks.
		onTestValuesReset?.(resetValues);
	}, [flattenedTags, generateSampleValue, resolveFromMergeTags, onTestValuesReset]);

	if (!isVisible) return null;

	const modalBackdropClassName = clsx([
		'test-variables-modal-backdrop',
		isVisible ? 'test-variables-modal-backdrop--visible' : '',
	]);

	const modalClassName = clsx([
		'test-variables-modal',
		isVisible ? 'test-variables-modal--visible' : '',
	]);

	return createPortal(
		<>
			{/* Backdrop */}
			<div
				className={modalBackdropClassName}
				onClick={onClose}
			/>

			{/* Modal */}
			<div className={modalClassName}>
				{/* Header */}
				<div className='test-variables-modal__header'>
					<div className='test-variables-modal__header-left'>
						<h3 className='test-variables-modal__title'>Edit Test Variables</h3>
						<p className='test-variables-modal__subtitle'>
							Customize dynamic field values to test your email with real-looking data
						</p>
					</div>
					<div className='test-variables-modal__header-right'>
						<Stack spacing='tight'>
							<Button
								onClick={handleReset}
								noBorder
							>
								<IconFont iconName='icon-refresh' />
								Reset to Defaults
							</Button>
							<Button
								onClick={onClose}
								noBorder
							>
								<IconFont iconName='icon-close' />
							</Button>
						</Stack>
					</div>
				</div>

				{/* Content */}
				<div className='test-variables-modal__content'>
					{Object.entries(groupedTags).map(([groupName, tags]) => {
						const listingImageTags = tags.filter(tag =>
							/^listing\.image_data_\d+$/.test(tag.key),
						);
						const nonListingTags = tags.filter(
							tag => !/^listing\.image_data_\d+$/.test(tag.key),
						);

						// Split the User Data group into Profile / Office sub-sections;
						// anything unclassified (non-user groups) stays under the main header.
						const profileTags = nonListingTags
							.filter(tag => getUserSubgroup(tag.key) === 'profile')
							.sort(
								(a, b) =>
									getFieldOrder(PROFILE_FIELD_ORDER, a.key) -
									getFieldOrder(PROFILE_FIELD_ORDER, b.key),
							);
						const officeTags = nonListingTags
							.filter(tag => getUserSubgroup(tag.key) === 'office')
							.sort(
								(a, b) =>
									getFieldOrder(OFFICE_FIELD_ORDER, a.key) -
									getFieldOrder(OFFICE_FIELD_ORDER, b.key),
							);
						const mainTags = nonListingTags.filter(
							tag => getUserSubgroup(tag.key) === null,
						);

						const renderField = ({
							key,
							label,
						}: {
							key: string;
							label: string;
						}) => {
							const isReadOnlyToken = isDownstreamResolvedMergeTagKey(key);

							return (
								<div
									key={key}
									className='test-variables-modal__field'
								>
									<label className='test-variables-modal__field-label'>{label}</label>
									{isReadOnlyToken ? (
										<div className='test-variables-modal__field-tag'>{`{{${key}}}`}</div>
									) : (
										<input
											type='text'
											className='test-variables-modal__field-input'
											value={localValues[key] || ''}
											onChange={e => handleInputChange(key, e.target.value)}
											placeholder={`Sample ${label}`}
										/>
									)}
								</div>
							);
						};

						const renderSubgroup = (
							title: string,
							subgroupTags: Array<{ key: string; label: string }>,
						) =>
							subgroupTags.length > 0 ? (
								<React.Fragment key={title}>
									<hr className='test-variables-modal__subgroup-divider' />
									<h5 className='test-variables-modal__subgroup-title'>{title}</h5>
									<div className='test-variables-modal__fields'>
										{subgroupTags.map(renderField)}
									</div>
								</React.Fragment>
							) : null;

						return (
							<div
								key={groupName}
								className='test-variables-modal__group'
							>
								<h4 className='test-variables-modal__group-title'>{groupName}</h4>
								{mainTags.length > 0 && (
									<div className='test-variables-modal__fields'>
										{mainTags.map(renderField)}
									</div>
								)}
								{renderSubgroup('Profile', profileTags)}
								{renderSubgroup('Office', officeTags)}
								{renderSubgroup('Listing images', listingImageTags)}
							</div>
						);
					})}
				</div>

				{/* Footer */}
				<div className='test-variables-modal__footer'>
					<Stack
						spacing='loose'
						distribution='trailing'
					>
						<button	className="btn btn-outline-secondary"
							onClick={onClose}
						>
							Cancel
						</button>
						<button className="btn btn-primary" onClick={handleSave}>Save & Apply</button>
					</Stack>
				</div>
			</div>
		</>,
		document.body,
	);
}
